import { useAuth } from '@xhubio-saas/core-frontend/kernel'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  StatusBadge,
  toast,
} from '@xhubio-saas/core-frontend/ui-components'
import { BellIcon, Loader2, SendHorizonal, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { trpc } from '@/trpc'
import { entwertePostausgang } from '../../../api/postausgang'
import { SettingsLayout } from '../../../layouts/SettingsLayout'

/**
 * Benachrichtigungen (`/settings/notifications`) — geplante Berichte und
 * Alarme (Plan CORE 2026-08-20/04, Schritt 5).
 *
 * ## Der Satz-Baukasten
 *
 * Die Fläche ist bewusst **ein Satz mit Auswahlfeldern** und kein Formular mit
 * zwölf Feldern: „Schicke mir <Bericht> <täglich> als <E-Mail>." Alles darin
 * sind geschlossene Mengen, die das Backend liefert (`report.listDefinitions`,
 * `report.capabilities`) — es gibt keinen Freitext, mit dem sich eine Abfrage
 * ins Backend schmuggeln liesse.
 *
 * ## 🔴 Nur Ids und Eingaben reisen
 *
 * Jede Mutation bekommt die **Id** und genau das, was der Mensch angefasst hat.
 * Insbesondere schickt das Ändern einer Kadenz nicht das ganze Abonnement und
 * schon gar nicht die Liste aller Abonnements zurück (CLAUDE.md, „Was der
 * Server weiss, schickt ihm der Browser nicht zurück"). `updateSubscription`
 * nimmt ein Abo, nicht die Liste.
 *
 * ## 🔴 Kein toter Tab
 *
 * Ist `scheduledReports` im Tarif nicht enthalten, zeigt die Seite **warum**
 * und wohin — nicht eine leere Fläche. Ein leerer Tab ohne Erklärung ist der
 * Standardfehler an dieser Stelle.
 */
/**
 * Farbe des letzten Laufs, wie im Berichts-Plugin (`frontend-plugin-report`
 * `ReportsPage.tsx`, `badgeVariantOf`): `sent-partial` (report-backend ≥ 1.7.0,
 * Plan ALLGEMEIN 09-26/05 S8) ist kein voller Erfolg — ein Teil der Empfänger
 * wurde mangels Leserecht ausgelassen — und verlangt einen Blick, also
 * `pending`. `lastStatus` ist ein Textwert über den Draht, kein Enum.
 */
export function statusVariantOf(lastStatus: string): 'success' | 'pending' | 'neutral' {
  if (lastStatus === 'sent') return 'success'
  if (lastStatus === 'held' || lastStatus === 'sent-partial') return 'pending'
  return 'neutral'
}

const DEFINITION_LABEL_KEYS: Record<string, string> = {
  'invoice-api.audit-failures': 'settings.notifications.definition.auditFailures',
  'invoice-api.usage-summary': 'settings.notifications.definition.usageSummary',
}

export function SettingsNotificationsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const utils = trpc.useUtils()

  const capabilities = trpc.report.capabilities.useQuery()
  const definitions = trpc.report.listDefinitions.useQuery()
  const subscriptions = trpc.report.listSubscriptions.useQuery()

  const [reportKey, setReportKey] = useState<string>('')
  // Plan CORE 09-26/16 S6 — die Empfängerliste kennt den gewählten Bericht und
  // sagt je Mitglied, ob es ihn lesen darf (`permitted`, report-backend ≥ 1.7.0).
  // Ohne Bericht fragt die Seite wie bisher (alle `permitted: true`).
  const recipients = trpc.report.listRecipients.useQuery(reportKey ? { reportKey } : undefined)
  const [cadence, setCadence] = useState<string>('')
  const [output, setOutput] = useState<'email' | 'pdf'>('email')
  const [picked, setPicked] = useState<string[]>([])

  const invalidate = async () => {
    await utils.report.listSubscriptions.invalidate()
  }

  const create = trpc.report.createSubscription.useMutation({
    onSuccess: async () => {
      await invalidate()
      toast.success(t('settings.notifications.created', 'Bericht aktiviert'))
    },
  })

  const update = trpc.report.updateSubscription.useMutation({
    onSuccess: async () => {
      await invalidate()
      toast.success(t('settings.notifications.updated', 'Änderung gespeichert'))
    },
  })

  const remove = trpc.report.deleteSubscription.useMutation({
    onSuccess: async () => {
      await invalidate()
      toast.success(t('settings.notifications.removed', 'Bericht abbestellt'))
    },
  })

  const runNow = trpc.report.runNow.useMutation({
    onSuccess: async (result) => {
      await invalidate()
      // Der Testlauf schickt in der Kategorie `report`; steht sie auf „zurückhalten", liegt die Mail in der Freigabe.
      await entwertePostausgang(utils)
      const status = result.status
      if (status === 'sent') {
        toast.success(
          t('settings.notifications.testSent', 'Testbericht verschickt — an {{count}} Empfänger', {
            count: result.delivered,
          })
        )
      } else if (status === 'sent-partial') {
        // Verschickt, aber nicht an alle: ein Empfänger ohne Leserecht wurde
        // ausgelassen. Kein Erfolg — die Warnung bleibt stehen.
        toast.warning(
          t(
            'settings.notifications.testPartlySent',
            'Testbericht an {{count}} Empfänger verschickt — Empfänger ohne Leserecht ausgelassen',
            { count: result.delivered }
          )
        )
      } else {
        // 🔴 „empty", „not-delivered", „no-recipients" sind keine Erfolge. Sie
        // bleiben stehen, bis jemand sie liest (Toast-Regel: Fehler persistent).
        // Plan API 09-26/11 S4: der Status in der Sprache der Seite — dieselbe
        // Beschriftung wie das Abzeichen am Abo, nie der Code über den Draht.
        toast.warning(
          t('settings.notifications.testNotSent', 'Nichts verschickt ({{status}}) {{message}}', {
            status: statusLabel(status),
            message: result.message ?? '',
          })
        )
      }
    },
  })

  const gesperrt = capabilities.data?.scheduledReports === false
  const pdfMoeglich = capabilities.data?.pdfAvailable === true
  const laedt =
    capabilities.isLoading ||
    definitions.isLoading ||
    recipients.isLoading ||
    subscriptions.isLoading

  const gewaehlteDefinition = definitions.data?.find((entry) => entry.key === reportKey)
  const kadenzen = gewaehlteDefinition?.selectableCadences ?? ['daily', 'weekly', 'monthly']
  const empfaengerListe = recipients.data ?? []
  // Wer den gewählten Bericht nicht lesen darf, bleibt sichtbar, ist aber nicht
  // wählbar — auch nicht als Vorbelegung (die eigene Person) oder als Auswahl
  // von einem vorher gewählten Bericht. Der Server bleibt der Riegel.
  const nichtLesend = new Set(empfaengerListe.filter((p) => !p.permitted).map((p) => p.userId))
  const empfaenger = (picked.length > 0 ? picked : user?.id ? [user.id] : []).filter(
    (id) => !nichtLesend.has(id)
  )

  const kadenzLabel = (value: string) =>
    ({
      daily: t('settings.notifications.cadence.daily', 'täglich'),
      weekly: t('settings.notifications.cadence.weekly', 'wöchentlich'),
      monthly: t('settings.notifications.cadence.monthly', 'monatlich'),
      hourly: t('settings.notifications.cadence.hourly', 'stündlich'),
    })[value] ?? value

  const statusLabel = (value: string) =>
    t(`settings.notifications.status.${value}`, { defaultValue: value })

  const ausgabeLabel = (value: string) =>
    value === 'pdf'
      ? t('settings.notifications.output.pdf', 'PDF-Anhang')
      : t('settings.notifications.output.email', 'E-Mail-Text')

  const berichtLabel = (key: string) => {
    // 🔴 Die Berichts-Schlüssel des Backends tragen selbst einen Punkt
    // (`invoice-api.audit-failures`) — als i18n-Schlüssel eingesetzt würden sie
    // zu einer verschachtelten Ebene, die niemand im Sprachbündel vermutet.
    // Deshalb eine ausdrückliche Zuordnung: sie ist lesbar, und ein neuer
    // Bericht ohne Eintrag zeigt seinen Schlüssel statt einer leeren Fläche.
    const i18nKey = DEFINITION_LABEL_KEYS[key]
    return i18nKey ? t(i18nKey, key) : key
  }

  const anlegen = () => {
    if (!reportKey || empfaenger.length === 0) return
    create.mutate({
      reportKey,
      cadence: (cadence || gewaehlteDefinition?.defaultCadence || 'daily') as
        | 'daily'
        | 'weekly'
        | 'monthly',
      output,
      recipients: empfaenger,
    })
  }

  return (
    <SettingsLayout>
      <div className="flex flex-col gap-6" data-testid="settings-notifications-page">
        <Card className="w-full">
          <CardHeader>
            <div className="flex gap-4">
              <BellIcon className="text-muted-foreground" />
              <CardTitle>{t('settings.notifications.tab', 'Benachrichtigungen')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <p className="text-sm text-muted-foreground">
              {t(
                'settings.notifications.intro',
                'Lass dir wiederkehrende Auswertungen automatisch schicken, statt sie zu suchen. Der Link in der Mail führt direkt in die passende Ansicht.'
              )}
            </p>

            {laedt && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('settings.notifications.loading', 'Wird geladen …')}
              </div>
            )}

            {/* 🔴 Kein leerer Tab: der Grund steht da, nicht nichts. */}
            {!laedt && gesperrt && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-700 dark:bg-amber-950"
                data-testid="reports-plan-locked"
              >
                <p className="font-medium">
                  {t(
                    'settings.notifications.locked.title',
                    'Geplante Berichte gehören zu einem größeren Tarif'
                  )}
                </p>
                <p className="mt-1 text-muted-foreground">
                  {t(
                    'settings.notifications.locked.body',
                    'Dein aktueller Tarif enthält keine geplanten Berichte. Bereits eingerichtete Berichte bleiben sichtbar und lassen sich abbestellen.'
                  )}
                </p>
                {/* Ein Hinweis auf einen größeren Tarif ohne Weg dorthin ist eine
                    Sackgasse — bis 2026-09-21 endete der Kasten genau hier. */}
                <Link
                  to="/subscription"
                  className="mt-3 inline-flex items-center rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground text-sm hover:bg-primary/90"
                  data-testid="reports-plan-upgrade"
                >
                  {t('settings.notifications.locked.cta', 'Tarife ansehen')}
                </Link>
              </div>
            )}

            {!laedt && !gesperrt && (
              <div className="flex flex-col gap-4" data-testid="reports-builder">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span>{t('settings.notifications.sentence.lead', 'Schicke mir')}</span>

                  <Select value={reportKey} onValueChange={setReportKey}>
                    <SelectTrigger className="w-[280px]" data-testid="reports-select-definition">
                      <SelectValue
                        placeholder={t(
                          'settings.notifications.sentence.pickReport',
                          'welchen Bericht?'
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(definitions.data ?? []).map((definition) => (
                        <SelectItem key={definition.key} value={definition.key}>
                          {berichtLabel(definition.key)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={cadence} onValueChange={setCadence}>
                    <SelectTrigger className="w-[160px]" data-testid="reports-select-cadence">
                      <SelectValue
                        placeholder={kadenzLabel(gewaehlteDefinition?.defaultCadence ?? 'daily')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {kadenzen.map((value) => (
                        <SelectItem key={value} value={value}>
                          {kadenzLabel(value)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <span>{t('settings.notifications.sentence.as', 'als')}</span>

                  <Select
                    value={output}
                    onValueChange={(value) => setOutput(value as 'email' | 'pdf')}
                  >
                    <SelectTrigger className="w-[180px]" data-testid="reports-select-output">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">{ausgabeLabel('email')}</SelectItem>
                      {/* Ohne PDF-Weg im Backend wäre die Auswahl eine Attrappe. */}
                      {pdfMoeglich && <SelectItem value="pdf">{ausgabeLabel('pdf')}</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <Label>{t('settings.notifications.recipients', 'Empfänger')}</Label>
                  <div className="flex flex-wrap gap-4">
                    {empfaengerListe.map((person) => {
                      const aktiv = empfaenger.includes(person.userId)
                      const darf = person.permitted
                      return (
                        <label
                          key={person.userId}
                          className="flex items-center gap-2 text-sm"
                          htmlFor={`reports-recipient-${person.userId}`}
                          data-testid={`reports-recipient-${person.userId}`}
                        >
                          <Checkbox
                            id={`reports-recipient-${person.userId}`}
                            checked={aktiv}
                            disabled={!darf}
                            onCheckedChange={(checked) =>
                              setPicked((current) => {
                                const basis = current.length > 0 ? current : empfaenger
                                return checked === true
                                  ? [...new Set([...basis, person.userId])]
                                  : basis.filter((id) => id !== person.userId)
                              })
                            }
                          />
                          {person.name ? `${person.name} · ${person.email}` : person.email}
                          {!darf && (
                            <span
                              className="text-muted-foreground"
                              data-testid={`reports-recipient-not-permitted-${person.userId}`}
                            >
                              (
                              {t(
                                'settings.notifications.recipientNotPermitted',
                                'ohne Leserecht für diesen Bericht'
                              )}
                              )
                            </span>
                          )}
                        </label>
                      )
                    })}
                  </div>
                </div>

                {/* UI-Guidelines: primäre Aktion rechts. */}
                <div className="flex justify-end">
                  <Button
                    onClick={anlegen}
                    disabled={!reportKey || empfaenger.length === 0 || create.isPending}
                    data-testid="reports-create"
                  >
                    {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('settings.notifications.activate', 'Bericht aktivieren')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t('settings.notifications.active.title', 'Deine Berichte')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {(subscriptions.data ?? []).length === 0 && !laedt && (
              <p className="text-sm text-muted-foreground" data-testid="reports-empty">
                {t('settings.notifications.active.empty', 'Noch kein Bericht aktiviert.')}
              </p>
            )}

            {(subscriptions.data ?? []).map((subscription, index) => (
              <div key={subscription.id} className="flex flex-col gap-3">
                {index > 0 && <Separator />}
                <div
                  className="flex flex-wrap items-center justify-between gap-4"
                  data-testid={`reports-subscription-${subscription.reportKey}`}
                >
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{berichtLabel(subscription.reportKey)}</span>

                    {/* Nur die Änderung reist: Id + neue Kadenz. */}
                    <Select
                      value={subscription.cadence}
                      onValueChange={(value) =>
                        update.mutate({
                          id: subscription.id,
                          cadence: value as 'daily' | 'weekly' | 'monthly',
                        })
                      }
                    >
                      <SelectTrigger
                        className="w-[150px]"
                        data-testid={`reports-cadence-${subscription.reportKey}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['daily', 'weekly', 'monthly'].map((value) => (
                          <SelectItem key={value} value={value}>
                            {kadenzLabel(value)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <span>{t('settings.notifications.sentence.as', 'als')}</span>

                    <Select
                      value={subscription.output}
                      onValueChange={(value) =>
                        update.mutate({ id: subscription.id, output: value as 'email' | 'pdf' })
                      }
                    >
                      <SelectTrigger
                        className="w-[170px]"
                        data-testid={`reports-output-${subscription.reportKey}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">{ausgabeLabel('email')}</SelectItem>
                        {pdfMoeglich && <SelectItem value="pdf">{ausgabeLabel('pdf')}</SelectItem>}
                      </SelectContent>
                    </Select>

                    {subscription.lastStatus && (
                      <StatusBadge
                        variant={statusVariantOf(subscription.lastStatus)}
                        data-testid={`reports-status-${subscription.reportKey}`}
                      >
                        {statusLabel(subscription.lastStatus)}
                      </StatusBadge>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => remove.mutate({ id: subscription.id })}
                      disabled={remove.isPending}
                      data-testid={`reports-delete-${subscription.reportKey}`}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('settings.notifications.remove', 'Abbestellen')}
                    </Button>
                    <Button
                      onClick={() => runNow.mutate({ id: subscription.id })}
                      disabled={runNow.isPending}
                      data-testid={`reports-run-now-${subscription.reportKey}`}
                    >
                      {runNow.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <SendHorizonal className="mr-2 h-4 w-4" />
                      )}
                      {t('settings.notifications.testNow', 'Jetzt testen')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  )
}
